import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';

import { type useGetAllUnreadNotifications_userNotifications$key } from './__generated__/useGetAllUnreadNotifications_userNotifications.graphql.ts';

type UnreadNotifications = {
    activityFeedIds: string[];
    itemEventIds: string[];
    conversationIds: string[];
};

const userNotificationsFragment = graphql`
    fragment useGetAllUnreadNotifications_userNotifications on UserNotifications {
        latest {
            all {
                notificationType
                payload {
                    ... on FavoriteNotification {
                        serviceId
                        unreadTag {
                            serviceId
                        }
                    }
                    ... on ActivityFeedElement {
                        serviceId
                        unreadTag {
                            serviceId
                        }
                    }
                }
            }
            messages {
                serviceId
            }
        }
        earlier {
            all {
                notificationType
                payload {
                    ... on FavoriteNotification {
                        serviceId
                        unreadTag {
                            serviceId
                        }
                    }
                    ... on ActivityFeedElement {
                        serviceId
                        unreadTag {
                            serviceId
                        }
                    }
                }
            }
        }
    }
`;

export const useGetAllUnreadNotifications = ({
    userNotifications: userNotificationsRef,
}: {
    userNotifications: useGetAllUnreadNotifications_userNotifications$key | null;
}): { unreadNotificationsCount: number; unreadNotificationsIds: UnreadNotifications } => {
    const userNotifications = useFragment(userNotificationsFragment, userNotificationsRef);
    const allNotifications = [
        userNotifications?.latest?.all,
        userNotifications?.earlier?.all,
    ].flat();
    const unreadConversations = useMemo(
        () => userNotifications?.latest?.messages || [],
        [userNotifications]
    );

    const activityFeedIds = useMemo(() => {
        return allNotifications
            .filter(
                notification =>
                    notification?.notificationType === 'ACTIVITY_FEED' &&
                    !!notification?.payload?.unreadTag
            )
            .map(notification => notification?.payload?.unreadTag?.serviceId ?? '');
    }, [allNotifications]);

    const itemEventIds = useMemo(() => {
        return allNotifications
            .filter(
                notification =>
                    notification?.notificationType === 'ITEM_EVENT' &&
                    !!notification?.payload?.unreadTag
            )
            .map(notification => notification?.payload?.unreadTag?.serviceId ?? '');
    }, [allNotifications]);

    const conversationIds = useMemo(() => {
        return unreadConversations.map(conversation => conversation?.serviceId ?? '');
    }, [unreadConversations]);

    const unreadNotificationsCount = useMemo(() => {
        return activityFeedIds.length + itemEventIds.length + conversationIds.length;
    }, [activityFeedIds, itemEventIds, conversationIds]);

    return {
        unreadNotificationsCount,
        unreadNotificationsIds: { activityFeedIds, itemEventIds, conversationIds },
    };
};
